// Payment schedule panel for an order (embedded in OrderDetail).
// Staff: set/override the schedule, generate Airwallex links, mark arrived, record a payment, remind.
// Client: generate/open the link to pay their own installment. Rep: read-only.
import { useEffect, useRef, useState } from 'react';
import { fmtMoney } from '../lib/matrix.js';
import { installmentTiming, paymentMethodLabel, termsLabel, paymentStatusLabel } from '../lib/labels.js';
import {
  fetchOrderPayments,
  setSchedule,
  markArrived,
  generateLink,
  recordPayment,
  remindNow,
  fetchSavedCards,
  saveCardLink,
  payWithSavedCard,
  removeSavedCard,
} from '../api/payments.js';

const ROLLUP = {
  UNPAID: { label: 'Unpaid', cls: 'bg-surface-raised text-text-secondary' },
  DEPOSIT_PAID: { label: 'Deposit paid', cls: 'bg-accent-light text-text-primary' },
  PART_PAID: { label: 'Part-paid', cls: 'bg-accent-light text-text-primary' },
  PAID_IN_FULL: { label: 'Paid in full', cls: 'bg-success-light text-success' },
};
const STATUS = {
  PENDING: { label: 'Unpaid', cls: 'text-text-muted' },
  LINK_SENT: { label: 'Unpaid', staffLabel: 'Link sent', cls: 'text-accent' },
  PAID: { label: 'Paid', cls: 'text-success' },
  VOID: { label: 'Cancelled', cls: 'text-text-muted line-through' },
};
const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';

export default function PaymentPanel({ orderId, role }) {
  const isStaff = role === 'INTERNAL_STAFF';
  const isClient = role === 'CLIENT';
  const canAct = isStaff || isClient; // reps are read-only
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const inFlight = useRef(false); // guards money actions against a double-fire before `busy` re-renders
  const [termType, setTermType] = useState('PAY_IN_FULL');
  const [depositPct, setDepositPct] = useState(50);
  const [netDays, setNetDays] = useState(30);
  const [netFrom, setNetFrom] = useState('INVOICE');
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'bank_transfer', note: '', receivedAt: '' });
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('');

  const load = () => fetchOrderPayments(orderId).then(setData).catch((e) => setError(errMsg(e)));
  const loadCards = () =>
    fetchSavedCards(orderId)
      .then((cs) => {
        setCards(cs);
        setSelectedCard((prev) => (cs.some((c) => c.id === prev) ? prev : cs[0]?.id || ''));
      })
      .catch(() => setCards([])); // reps/clients without cards, or Airwallex dormant — just no cards
  useEffect(() => {
    load();
    loadCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const run = async (key, fn) => {
    // Synchronous in-flight guard: the `busy`/`disabled` state can lag a render, so a fast double-click
    // (or two-tab) could otherwise fire a money action twice. This blocks the second call immediately.
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(key);
    setError('');
    try {
      const next = await fn();
      if (next && next.installments) setData(next);
      else await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      inFlight.current = false;
      setBusy('');
    }
  };

  const applySchedule = () => {
    const terms =
      termType === 'DEPOSIT_BALANCE'
        ? { type: 'DEPOSIT_BALANCE', depositPct: Number(depositPct) }
        : termType === 'NET_TERMS'
          ? { type: 'NET_TERMS', netDays: Number(netDays) }
          : termType === 'DEPOSIT_NET'
            ? { type: 'DEPOSIT_NET', depositPct: Number(depositPct), netDays: Number(netDays), netFrom }
            : { type: termType };
    return run('schedule', () => setSchedule(orderId, { terms }));
  };

  const openPay = (i) => {
    setPayFor(i.id);
    setPayForm({ amount: String(i.amount), method: 'bank_transfer', note: '', receivedAt: '' });
    setError('');
  };
  const submitPay = (i) =>
    run(`pay:${i.id}`, () =>
      recordPayment(i.id, {
        amount: Number(payForm.amount) || undefined,
        method: payForm.method,
        note: payForm.note || undefined,
        receivedAt: payForm.receivedAt ? new Date(payForm.receivedAt).toISOString() : undefined,
      }).then((r) => {
        setPayFor(null);
        return r;
      })
    );

  // Saved cards --------------------------------------------------------------
  // Pay an installment with the selected saved card (one click).
  // Charging a stored card moves money immediately — always confirm who/what/how much first.
  const payCard = (i) => {
    const c = cards.find((x) => x.id === selectedCard);
    const who = isClient ? '' : ` for ${data.company || 'this client'}`;
    if (!window.confirm(`Charge ${fmtMoney(i.amount, i.currency)} to ${c ? cardLabel(c) : 'the saved card'}${who}?`)) return;
    run(`card:${i.id}`, () => payWithSavedCard(i.id, selectedCard));
  };
  // Generate a hosted link that offers "save this card"; open it so the client can pay + save.
  const getSaveLink = (i) =>
    run(`save:${i.id}`, () =>
      saveCardLink(i.id).then((r) => {
        if (r.installment?.linkUrl) window.open(r.installment.linkUrl, '_blank', 'noopener');
        return r; // not a full payments view → run() reloads, so the "Open link" button also appears
      })
    );
  const removeCard = async (cardId) => {
    setBusy(`rmcard:${cardId}`);
    setError('');
    try {
      await removeSavedCard(orderId, cardId);
      await loadCards();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };
  const cardLabel = (c) => `${(c.brand || 'card').replace(/^\w/, (m) => m.toUpperCase())} ···· ${c.last4 || '????'}`;

  if (!data) {
    return (
      <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-text-primary">Payments</h3>
        {error ? (
          <div className="mt-2">
            <p className="text-xs text-error">{error}</p>
            <button
              onClick={() => {
                setError('');
                load();
              }}
              className="mt-1.5 text-xs font-medium text-accent hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-text-muted">Loading…</p>
        )}
      </div>
    );
  }

  const rollup = ROLLUP[data.paymentStatus] || ROLLUP.UNPAID;
  const hasPaid = data.installments.some((i) => i.status === 'PAID');
  const hasUnactivatedArrival = data.installments.some((i) => i.trigger === 'ON_ARRIVAL' && !i.activatedAt);
  const canEditSchedule = isStaff && !hasPaid;
  // Payment opens on approval — the shipping cost is settled as part of that review, so paying before
  // it banks the wrong total and freezes the schedule. The server refuses either way (ORDER_NOT_APPROVED);
  // this keeps the panel honest instead of showing a button that errors.
  const awaitingApproval = data.orderStatus === 'PENDING_APPROVAL';
  const canPay = canAct && data.orderStatus === 'APPROVED';

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Payments</h3>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${rollup.cls}`}>{paymentStatusLabel(data.paymentStatus)}</span>
      </div>
      <dl className="mb-3 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-text-muted">Terms</dt>
        <dd className="text-right text-text-primary">{termsLabel(data.termType)}</dd>
        <dt className="text-text-muted">Outstanding</dt>
        <dd className="text-right font-medium text-text-primary">{fmtMoney(data.outstanding, data.currency)}</dd>
        <dt className="text-text-muted">Paid</dt>
        <dd className="text-right text-text-primary">{fmtMoney(data.paidTotal, data.currency)}</dd>
        {data.refundedTotal > 0 && (
          <>
            <dt className="text-text-muted">Refunded</dt>
            <dd className="text-right text-text-primary">{fmtMoney(data.refundedTotal, data.currency)}</dd>
          </>
        )}
      </dl>

      {awaitingApproval && (
        <p className="mb-3 rounded-md border border-warning/40 bg-warning-light px-2.5 py-2 text-[11px] text-text-primary">
          {isClient
            ? 'Payment opens once TEAMM8 approves this order. The amounts below don’t include shipping yet — you’ll get an invoice and a payment link by email as soon as it’s confirmed.'
            : isStaff
              ? 'Payment is held until this order is approved. The amounts below exclude shipping — confirm shipping, then approve to open payment and email the invoice + link.'
              : 'Payment opens once TEAMM8 approves this order and confirms shipping. The client then gets the invoice and payment link by email.'}
        </p>
      )}

      {/* Schedule setup / override (staff) */}
      {canEditSchedule && (
        <div className="mb-3 rounded-md border border-line bg-surface-raised/40 p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={termType}
              onChange={(e) => setTermType(e.target.value)}
              className="rounded-md border border-line bg-surface px-2 py-1 text-xs outline-none focus:border-accent"
            >
              <option value="PAY_IN_FULL">Pay in full (before dispatch)</option>
              <option value="DEPOSIT_BALANCE">Deposit + balance on delivery</option>
              <option value="DEPOSIT_NET">Deposit + balance after X days</option>
              <option value="NET_TERMS">Net terms</option>
              <option value="PAY_ON_DELIVERY">Pay in full on delivery</option>
            </select>
            {termType === 'DEPOSIT_NET' && (
              <span className="flex flex-wrap items-center gap-1 text-xs text-text-secondary">
                <input type="number" min={1} max={99} value={depositPct} onChange={(e) => setDepositPct(e.target.value)} aria-label="Deposit percent" className="w-14 rounded border border-line px-1.5 py-1 text-xs" />% upfront, balance
                <input type="number" min={0} max={365} value={netDays} onChange={(e) => setNetDays(e.target.value)} aria-label="Days until balance is due" className="w-14 rounded border border-line px-1.5 py-1 text-xs" /> days after
                <select value={netFrom} onChange={(e) => setNetFrom(e.target.value)} aria-label="Balance clock starts at" className="rounded border border-line bg-surface px-1.5 py-1 text-xs">
                  <option value="INVOICE">invoice</option>
                  <option value="DISPATCH">dispatch</option>
                  <option value="DELIVERY">delivery</option>
                </select>
              </span>
            )}
            {termType === 'DEPOSIT_BALANCE' && (
              <label className="flex items-center gap-1 text-xs text-text-secondary">
                Deposit
                <input type="number" min={1} max={99} value={depositPct} onChange={(e) => setDepositPct(e.target.value)} className="w-14 rounded border border-line px-1.5 py-1 text-xs" />%
              </label>
            )}
            {termType === 'NET_TERMS' && (
              <label className="flex items-center gap-1 text-xs text-text-secondary">
                Net
                <input type="number" min={1} max={365} value={netDays} onChange={(e) => setNetDays(e.target.value)} className="w-16 rounded border border-line px-1.5 py-1 text-xs" /> days
              </label>
            )}
            <button
              onClick={applySchedule}
              disabled={busy === 'schedule'}
              className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy === 'schedule' ? 'Applying…' : data.installments.length ? 'Update schedule' : 'Set schedule'}
            </button>
          </div>
        </div>
      )}

      {/* Saved cards — stored securely by Airwallex; power one-click "Pay with saved card". */}
      {(cards.length > 0 || canAct) && (
        <div className="mb-3 rounded-md border border-line bg-surface-raised/40 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold text-text-primary">Saved cards</span>
            {isStaff && (
              <button onClick={loadCards} className="text-[11px] text-text-muted hover:text-text-primary hover:underline">
                Refresh
              </button>
            )}
          </div>
          {cards.length === 0 ? (
            <p className="text-[11px] text-text-muted">
              {isClient
                ? 'No saved cards yet. Choose “Pay & save card” below to pay faster next time — your card is stored securely by our payment provider, never by us.'
                : 'No saved cards yet.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {cards.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 rounded border border-line bg-surface px-2 py-1 text-xs">
                  <label className="flex items-center gap-2">
                    {canAct && (
                      <input type="radio" name="savedcard" checked={selectedCard === c.id} onChange={() => setSelectedCard(c.id)} className="accent-accent" />
                    )}
                    <span className="text-text-primary">{cardLabel(c)}</span>
                    {c.expMonth && c.expYear && (
                      <span className="text-text-muted">· exp {String(c.expMonth).padStart(2, '0')}/{String(c.expYear).slice(-2)}</span>
                    )}
                  </label>
                  {canAct && (
                    <button
                      onClick={() => removeCard(c.id)}
                      disabled={busy === `rmcard:${c.id}`}
                      className="text-[11px] text-error hover:underline disabled:opacity-50"
                    >
                      {busy === `rmcard:${c.id}` ? '…' : 'Remove'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Installments */}
      <ul className="space-y-2">
        {data.installments.map((i) => {
          const st = STATUS[i.status] || STATUS.PENDING;
          const activated = Boolean(i.activatedAt);
          return (
            <li key={i.id} className="rounded-md border border-line p-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary">{i.label}</span>
                <span className="tabular-nums font-medium text-text-primary">{fmtMoney(i.amount, i.currency)}</span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-text-muted">
                <span>{installmentTiming(i)}</span>
                <span className={st.cls}>{isStaff ? st.staffLabel || st.label : st.label}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {i.hasLink && i.linkUrl && i.status !== 'PAID' && i.status !== 'VOID' && !isClient && (
                  <a
                    href={i.linkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded border border-line px-2 py-1 font-medium text-accent hover:bg-surface-raised"
                  >
                    Open payment link
                  </a>
                )}
                {/* Client: ONE clear way to pay (opens the secure payment page). */}
                {isClient && canPay && i.status !== 'PAID' && i.status !== 'VOID' && activated && (
                  <button
                    onClick={() =>
                      i.linkUrl
                        ? window.open(i.linkUrl, '_blank', 'noopener')
                        : run(`link:${i.id}`, () =>
                            generateLink(i.id).then((r) => {
                              if (r.installment?.linkUrl) window.open(r.installment.linkUrl, '_blank', 'noopener');
                              return r;
                            })
                          )
                    }
                    disabled={busy === `link:${i.id}`}
                    className="rounded bg-accent px-2.5 py-1 font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
                  >
                    {busy === `link:${i.id}` ? 'Opening…' : `Pay ${fmtMoney(i.amount, i.currency)}`}
                  </button>
                )}
                {/* Generate a one-off link: staff any activated unpaid; client for their own. */}
                {isStaff && canPay && i.status !== 'PAID' && activated && (
                  <button
                    onClick={() => run(`link:${i.id}`, () => generateLink(i.id))}
                    title={i.hasLink ? 'Replace the payment link with a fresh one (the old link stops working)' : 'Create a payment link for this amount'}
                    disabled={busy === `link:${i.id}`}
                    className="rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
                  >
                    {busy === `link:${i.id}` ? '…' : i.hasLink ? 'New link' : 'Create link'}
                  </button>
                )}
                {/* One-click charge of the selected saved card. */}
                {canPay && i.status !== 'PAID' && activated && cards.length > 0 && selectedCard && (
                  <button
                    onClick={() => payCard(i)}
                    disabled={busy === `card:${i.id}`}
                    className="rounded border border-accent px-2 py-1 font-medium text-accent hover:bg-accent-light disabled:opacity-50"
                  >
                    {busy === `card:${i.id}` ? 'Charging…' : `Pay with ${(() => { const c = cards.find((x) => x.id === selectedCard); return c ? cardLabel(c) : 'saved card'; })()}`}
                  </button>
                )}
                {/* Hosted link that also offers "save this card" for future one-click payment. */}
                {canPay && i.status !== 'PAID' && activated && (
                  <button
                    onClick={() => getSaveLink(i)}
                    disabled={busy === `save:${i.id}`}
                    className="rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
                  >
                    {busy === `save:${i.id}` ? '…' : 'Pay & save card'}
                  </button>
                )}
                {/* Staff can always record money that actually arrived (e.g. an early bank transfer),
                    even on an installment that isn't activated yet — the server has no such gate. */}
                {isStaff && i.status !== 'PAID' && (
                  <>
                    <button
                      onClick={() => openPay(i)}
                      disabled={busy === `pay:${i.id}`}
                      className="rounded border border-line px-2 py-1 font-medium text-success hover:bg-success-light disabled:opacity-50"
                    >
                      Mark paid
                    </button>
                    {i.hasLink && (
                      <button
                        onClick={() => run(`remind:${i.id}`, () => remindNow(i.id))}
                        disabled={busy === `remind:${i.id}`}
                        className="rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
                      >
                        {busy === `remind:${i.id}` ? '…' : 'Remind'}
                      </button>
                    )}
                  </>
                )}
                {i.paidAt && i.paidMethod && <span className="px-1 py-1 text-text-muted">{paymentMethodLabel(i.paidMethod)}</span>}
                {i.refundedAmount > 0 && <span className="px-1 py-1 text-text-muted">· {fmtMoney(i.refundedAmount, i.currency)} refunded</span>}
                {!isClient && i.remindersSent > 0 && <span className="px-1 py-1 text-text-muted">· {i.remindersSent} reminder{i.remindersSent > 1 ? 's' : ''} sent</span>}
              </div>

              {/* Manual mark-as-paid form (CS) — method, amount (supports partial), date, note. */}
              {isStaff && payFor === i.id && (
                <div className="mt-2 rounded-md border border-line bg-surface-raised/40 p-2">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-text-secondary">
                      Amount
                      <input type="number" min={0} step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-0.5 block w-full rounded border border-line px-1.5 py-1 text-xs" />
                    </label>
                    <label className="text-[11px] text-text-secondary">
                      Method
                      <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="mt-0.5 block w-full rounded border border-line bg-surface px-1.5 py-1 text-xs">
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="cheque">Cheque</option>
                        <option value="cash">Cash</option>
                        <option value="offline_card">Offline card</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="text-[11px] text-text-secondary">
                      Date received
                      <input type="date" value={payForm.receivedAt} onChange={(e) => setPayForm({ ...payForm, receivedAt: e.target.value })} className="mt-0.5 block w-full rounded border border-line px-1.5 py-1 text-xs" />
                    </label>
                    <label className="text-[11px] text-text-secondary">
                      Note
                      <input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="ref / memo" className="mt-0.5 block w-full rounded border border-line px-1.5 py-1 text-xs" />
                    </label>
                  </div>
                  {Number(payForm.amount) > 0 && Number(payForm.amount) < i.amount && (
                    <p className="mt-1 text-[11px] text-text-muted">Partial — {fmtMoney(i.amount - Number(payForm.amount), i.currency)} will stay outstanding.</p>
                  )}
                  <div className="mt-2 flex gap-1.5">
                    <button onClick={() => setPayFor(null)} className="rounded border border-line px-2 py-1 text-xs text-text-secondary hover:bg-surface">Cancel</button>
                    <button onClick={() => submitPay(i)} disabled={busy === `pay:${i.id}`} className="rounded bg-success px-2 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50">
                      {busy === `pay:${i.id}` ? 'Recording…' : 'Record payment'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
        {data.installments.length === 0 && <li className="text-xs text-text-muted">No schedule yet.</li>}
      </ul>

      {isStaff && hasUnactivatedArrival && (
        <button
          onClick={() => run('arrived', () => markArrived(orderId))}
          disabled={busy === 'arrived'}
          className="mt-3 w-full rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
        >
          {busy === 'arrived' ? 'Marking…' : 'Mark delivered — balance now due'}
        </button>
      )}

      {error && <p className="mt-2 text-xs text-error">{error}</p>}
    </div>
  );
}
